/**
 * Timing Constants - Single source of truth for timeouts, retries and polling
 *
 * RULES:
 * 1. ALWAYS use these constants instead of hardcoded values
 * 2. NEVER define timeouts/retries in other files
 * 3. If you need a new value, add it here first
 */

// =============================================================================
// POLLING INTERVALS
// =============================================================================

export const POLLING_INTERVALS = {
  /** 1 second - For fast operations */
  FAST: 1000,
  /** 2 seconds - Default for most cases */
  NORMAL: 2000,
  /** 3 seconds - For moderate polling (e.g., video generation) */
  MODERATE: 3000,
  /** 5 seconds - For less urgent operations */
  SLOW: 5000,
  /** 10 seconds - For long-running jobs */
  VERY_SLOW: 10000,
  /** 30 seconds - For health checks */
  HEALTH_CHECK: 30000,
  /** 1 minute - For periodic checks */
  PERIODIC: 60000,
} as const;

// =============================================================================
// RETRY CONFIGURATIONS
// =============================================================================

export const RETRY_CONFIGS = {
  /** Quick: 3 retries, 1s delay - For simple operations */
  QUICK: {
    maxRetries: 3,
    delayMs: 1000,
  },
  /** Standard: 3 retries, 2s delay - For most cases */
  STANDARD: {
    maxRetries: 3,
    delayMs: 2000,
  },
  /** Patient: 5 retries, 1s delay - For setup/connection */
  PATIENT: {
    maxRetries: 5,
    delayMs: 1000,
  },
  /** Long running: 3600 attempts, 1s - For video generation (1 hour max) */
  LONG_RUNNING: {
    maxAttempts: 3600,
    intervalMs: 1000,
  },
  /** Transcription: 150 attempts, 2s - For audio transcription */
  TRANSCRIPTION: {
    maxAttempts: 150,
    intervalMs: 2000,
  },
  /** SSE: 3 retries, 10s delay - For SSE connection retries */
  SSE: {
    maxRetries: 3,
    delayMs: 10000,
  },
  /** Narration video: 200 attempts, 3s - For narration video generation */
  NARRATION_VIDEO: {
    maxAttempts: 200,
    intervalMs: 3000,
  },
  /** WebSocket: 3 retries with exponential backoff */
  WEBSOCKET: {
    maxRetries: 3,
    baseDelayMs: 5000,
    maxDelayMs: 30000,
  },
  /** GPU calibration: 2 auto-retries, no delay - each attempt already takes
   * up to a few minutes (binary-search FFmpeg benchmark), so retries run
   * back-to-back instead of waiting an artificial delay in between. */
  GPU_CALIBRATION: {
    maxRetries: 2,
    delayMs: 0,
  },
} as const;

// =============================================================================
// CACHE TTL (Time To Live)
// =============================================================================

export const CACHE_TTL = {
  /** 30 seconds - For frequently changing data */
  SHORT: 30000,
  /** 1 minute - Default */
  MEDIUM: 60000,
  /** 5 minutes - For more stable data */
  LONG: 300000,
  /** 15 minutes - For rarely changed data */
  VERY_LONG: 900000,
  /** 24 hours - For static data like styles */
  DAY: 24 * 60 * 60 * 1000,
} as const;

// =============================================================================
// TIMEOUTS
// =============================================================================

export const TIMEOUTS = {
  /** 30 seconds - For normal requests */
  REQUEST: 30000,
  /** 60 seconds - For SSE connection timeout */
  SSE_CONNECTION: 60000,
  /** 60 seconds - For longer operations */
  LONG_REQUEST: 60000,
  /** 5 minutes - For uploads */
  UPLOAD: 300000,
  /** 10 minutes - For heavy processing */
  PROCESSING: 600000,
  /**
   * 1 hour - Default for the video-generation API adapters. A generation
   * request can hold the connection for as long as the render runs.
   */
  GENERATION_API: 3600000,
  /** 2 seconds - For copy-to-clipboard feedback reset */
  COPY_FEEDBACK: 2000,
  /** 30 seconds - For auto-save intervals */
  AUTO_SAVE_INTERVAL: 30000,
  /** 3 seconds - For typing indicator visibility */
  TYPING_INDICATOR: 3000,
  /** 5 seconds - For spin wheel animation duration */
  SPIN_ANIMATION: 5000,
  /** 5 seconds - For toast auto-close default */
  TOAST_AUTO_CLOSE: 5000,
  /**
   * 10 seconds - For reading duration metadata off an <audio>/<video> element.
   * A container the renderer cannot demux may never fire `loadedmetadata` nor
   * `error`, so every metadata read must be bounded or the caller hangs forever.
   */
  MEDIA_METADATA: 10000,
} as const;

// =============================================================================
// DEBOUNCE INTERVALS
// =============================================================================

export const DEBOUNCE = {
  /** 300ms - Default debounce for input/search handlers */
  DEFAULT: 300,
  /** 400ms - Slower debounce for heavier operations */
  SLOW: 400,
} as const;

// =============================================================================
// FAILURE THRESHOLDS
// =============================================================================

export const FAILURE_THRESHOLDS = {
  /** Stop after 3 consecutive failures */
  STOP_AFTER_FAILURES: 3,
  /** Maximum failures before giving up */
  MAX_CONSECUTIVE_FAILURES: 5,
} as const;

// =============================================================================
// AUTH (Firebase ID token)
// =============================================================================

export const AUTH = {
  /**
   * 5 minutes - Force-refresh the Firebase ID token when it is within this
   * window of expiring. Firebase tokens live for 1h (FIREBASE_TOKEN_EXPIRY_SECONDS)
   * and the SDK's background proactive-refresh is unreliable in Electron (Chromium
   * throttles background timers; machine sleep stops them). Refreshing early
   * prevents shipping a near-expiry token that dies in flight to the server
   * (network latency + client/server clock skew -> "auth/token-expired").
   */
  TOKEN_REFRESH_SAFETY_WINDOW_MS: 5 * 60 * 1000,
} as const;
